import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminOrderActionRequest,
  AdminOrderActionResponse,
} from '@/lib/api-admin/types';
import { adminFetch } from '@/lib/apiClient';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { orderQueryKey } from './useOrderQuery';

/**
 * 관리자 구매확정 액션. be-order-lifecycle 계약:
 * `POST /api/admin/v1/orders/{orderId}/confirm-purchase` body=AdminOrderActionRequest
 * {reason 필수} + Idempotency-Key, @PreAuthorize SUPER_ADMIN/OPS_ADMIN.
 * → AdminOrderActionResponse{orderStatus:COMPLETED, displayStatus:CONFIRMED}.
 *
 * 경로가 generated OpenAPI 스펙에 미반영이라(useOrderActionMutation 의 5개 액션과 달리)
 * shipping/policy-settings 와 동일하게 adminFetch 로 직접 호출한다.
 * 게이트웨이 `/admin/v1/orders/**` RewritePath 재사용. allowlist 의 2-세그먼트
 * `POST /admin/v1/orders/{id}/{action}` 패턴에 자동 포함된다.
 */
interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

interface SuccessEnvelopeShape {
  success: true;
  data: AdminOrderActionResponse;
  meta?: ApiMeta;
}

export interface ConfirmPurchaseInput {
  orderId: string;
  body: AdminOrderActionRequest;
}

export const useConfirmPurchaseMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminOrderActionResponse, Error, ConfirmPurchaseInput>({
    mutationFn: async ({ orderId, body }) => {
      const path = `/admin/v1/orders/${orderId}/confirm-purchase`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', path, body),
        },
        body: JSON.stringify(body),
      });
      const raw = (await res.json().catch(() => null)) as
        | SuccessEnvelopeShape
        | ErrorEnvelopeShape
        | null;
      if (!res.ok || !raw || raw.success !== true) {
        const env = raw as ErrorEnvelopeShape | null;
        if (env && env.success === false && env.error) {
          throw new ApiClientError(env.error, {
            status: res.status,
            ...(env.meta ? { meta: env.meta } : {}),
          });
        }
        throw new TransportError(
          `Confirm purchase failed with status ${res.status}`,
          raw,
        );
      }
      return raw.data;
    },
    onSuccess: async (_data, { orderId }) => {
      await qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
  });
};
